import { NotificationVariants, type ToastProps } from '@talos/kyoko';
import {
  acceptQuote,
  type AcceptQuoteDeps,
  type AcceptQuoteFailure,
  type AcceptQuoteParams,
  type AcceptQuoteResult,
} from './acceptQuote';

type AcceptMultipleQuotesStatus = 'fulfilled' | 'rejected';

export interface AcceptMultipleQuotesFulfilledResult {
  status: Extract<AcceptMultipleQuotesStatus, 'fulfilled'>;
  params: AcceptQuoteParams;
  value: AcceptQuoteResult;
}

export interface AcceptMultipleQuotesRejectedResult {
  status: Extract<AcceptMultipleQuotesStatus, 'rejected'>;
  params: AcceptQuoteParams;
  reason: AcceptQuoteFailure;
}

export type AcceptMultipleQuotesResult = AcceptMultipleQuotesFulfilledResult | AcceptMultipleQuotesRejectedResult;

export interface AcceptMultipleQuotesSummary {
  totalCount: number;
  successfulCount: number;
  failedCount: number;
  results: AcceptMultipleQuotesResult[];
}

export interface AcceptMultipleQuotesDeps extends AcceptQuoteDeps {
  addToast: (toast: ToastProps) => void;
}

const DEFAULT_FAILURE: AcceptQuoteFailure = {
  text: 'An error occurred while accepting the quote. Please try again.',
};

function isAcceptQuoteFailure(reason: unknown): reason is AcceptQuoteFailure {
  return reason != null && typeof reason === 'object' && 'text' in reason && typeof reason.text === 'string';
}

/**
 * Runs multiple acceptQuote calls concurrently, reports each failure, and returns
 * the full settled status list for callers that need to update local state.
 */
export async function acceptMultipleQuotes(
  paramsList: AcceptQuoteParams[],
  { addToast, ...acceptQuoteDeps }: AcceptMultipleQuotesDeps
): Promise<AcceptMultipleQuotesSummary> {
  if (paramsList.length === 0) {
    return {
      totalCount: 0,
      successfulCount: 0,
      failedCount: 0,
      results: [],
    };
  }

  const settledResults = await Promise.allSettled(paramsList.map(params => acceptQuote(params, acceptQuoteDeps)));

  const results: AcceptMultipleQuotesResult[] = settledResults.map((result, index) => {
    const params = paramsList[index];

    if (result.status === 'fulfilled') {
      return {
        status: result.status,
        params,
        value: result.value,
      };
    }

    return {
      status: result.status,
      params,
      reason: normalizeFailure(result.reason),
    };
  });

  const successfulCount = results.filter(result => result.status === 'fulfilled').length;
  const failedResults = results.filter(
    (result): result is AcceptMultipleQuotesRejectedResult => result.status === 'rejected'
  );

  failedResults.forEach(({ reason }) => {
    addToast({
      text: reason.text,
      variant: reason.variant ?? NotificationVariants.Negative,
      dismissable: true,
      timeout: 5000,
    });
  });

  addToast({
    text: `${successfulCount}/${paramsList.length} quotes accepted successfully`,
    variant: getAggregateVariant(successfulCount, paramsList.length),
    dismissable: true,
    timeout: 5000,
  });

  return {
    totalCount: paramsList.length,
    successfulCount,
    failedCount: failedResults.length,
    results,
  };
}

function normalizeFailure(reason: unknown): AcceptQuoteFailure {
  if (isAcceptQuoteFailure(reason)) {
    return reason;
  }

  return DEFAULT_FAILURE;
}

function getAggregateVariant(successfulCount: number, totalCount: number): NotificationVariants {
  if (successfulCount === totalCount) {
    return NotificationVariants.Positive;
  }

  if (successfulCount > 0) {
    return NotificationVariants.Warning;
  }

  return NotificationVariants.Negative;
}
